import React, { useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { motion } from "framer-motion";
import { Package, ClipboardList, Truck, CheckCircle2, Search, MapPin, FileText, Clock as ClockIcon, AlertTriangle } from "lucide-react";
import {
  DocketEntryIcon,
  Manifestation,
  PickupIcon,
  TripsheetIcon,
} from "../assets/SvgConstant";
import { getDocketEntryById } from "../services/docketEntryService";
import { getDashboardStats } from "../services/dashboardService";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 100 },
  },
};

const Dashboard = () => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [searchNo, setSearchNo] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);

  // Real Dashboard State
  const [dashboardData, setDashboardData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const handleTrack = async () => {
    if (!searchNo.trim()) return;
    try {
      setLoadingTracking(true);
      const res = await getDocketEntryById(searchNo.trim().toUpperCase());
      const sortedHistory = res?.data?.history?.sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      setTrackingData({ ...res.data, history: sortedHistory });
    } catch (err) {
      alert("Docket not found");
      setTrackingData(null);
    } finally {
      setLoadingTracking(false);
    }
  };

  useEffect(() => {
    if (searchNo.trim() === "") {
      setTrackingData(null);
    }
  }, [searchNo]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        const branchQuery = user?.role === "EMPLOYEE" ? (user?.branch?.code || "") : "";
        const res = await getDashboardStats(branchQuery);
        if (res?.data?.success) {
          setDashboardData(res.data.data);
        }
      } catch (err) {
        console.error("Failed to load dashboard stats", err);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, [user]);

  const steps = ["CREATED", "MANIFESTED", "IN_TRANSIT", "DELIVERED"];

  // Fallback defaults for loading state
  const mockStats = [
    { title: "Active Pickups", value: "...", change: "0%", positive: true, icon: PickupIcon },
    { title: "Docker Utilization", value: "...", change: "0%", positive: true, icon: DocketEntryIcon },
    { title: "Pending Manifests", value: "...", change: "0%", positive: false, icon: Manifestation },
    { title: "Active Tripsheets", value: "...", change: "0%", positive: true, icon: TripsheetIcon },
    { title: "Stuck Dockets", value: "...", change: "Alert", positive: false, icon: <AlertTriangle className="w-5 h-5" color="#0369A1" />, path: "/stuck-dockets" },
  ];

  const buildStatsArray = (kpis) => {
    if (!kpis) return mockStats;
    return [
      {
        title: "Active Pickups",
        value: kpis.activePickups?.value || 0,
        change: kpis.activePickups?.change || "0%",
        positive: kpis.activePickups?.positive ?? true,
        icon: PickupIcon,
      },
      {
        title: "Docker Utilization",
        value: kpis.totalDockets?.value || 0,
        change: kpis.totalDockets?.change || "0%",
        positive: kpis.totalDockets?.positive ?? true,
        icon: DocketEntryIcon,
      },
      {
        title: "Pending Manifests",
        value: kpis.pendingManifests?.value || 0,
        change: kpis.pendingManifests?.change || "0%",
        positive: kpis.pendingManifests?.positive ?? false,
        icon: Manifestation,
      },
      {
        title: "Active Tripsheets",
        value: kpis.activeTripsheets?.value || 0,
        change: kpis.activeTripsheets?.change || "0%",
        positive: kpis.activeTripsheets?.positive ?? true,
        icon: TripsheetIcon,
      },
      {
        title: "Stuck Dockets",
        value: kpis.stuckDockets?.value ?? "View",
        change: kpis.stuckDockets?.change || "Check Now",
        positive: kpis.stuckDockets?.positive ?? false,
        icon: <AlertTriangle className="w-5 h-5" color="#0369A1" />,
        path: "/stuck-dockets",
      },
    ];
  };

  const dynamicStats = statsLoading ? mockStats : buildStatsArray(dashboardData?.kpis);
  const weeklyShipments = dashboardData?.charts?.weeklyShipments || [];
  const dockerVolume = dashboardData?.charts?.docketVolume || [];

  return (
    <div className="p-4 bg-slate-200 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-sm text-gray-500 mt-1">
            Welcome back, {user?.name || "Dispatcher"}. Here is your live summary.
          </p>
        </div>
        <div className="px-4 py-2 bg-white border rounded-lg text-sm shadow-sm font-semibold text-gray-600">
          📅 {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
          {dynamicStats.map((s, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              onClick={() => s.path && navigate(s.path)}
              className={`bg-white p-5 rounded-xl border shadow-sm relative overflow-hidden ${s.path ? "cursor-pointer hover:shadow-md" : ""}`}
            >
              {statsLoading && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              <div className="flex justify-between mb-3 items-center">
                <span className="text-sm text-gray-500 font-medium">{s.title}</span>
                <span className="w-10 h-10 flex items-center justify-center bg-blue-50/50 rounded-lg">
                  {typeof s.icon === "function" ? (() => {
                    const Icon = s.icon;
                    return <Icon className="w-5 h-5" fillColor="#0369A1" />;
                  })() : s.icon}
                </span>
              </div>
              <div className="flex items-end gap-2 mt-2">
                <h2 className="text-3xl font-bold text-gray-800">{s.value}</h2>
                <span className={`text-xs px-2 py-0.5 rounded font-bold ${s.positive ? "text-emerald-700 bg-emerald-100" : "text-rose-700 bg-rose-100"}`}>
                  {s.change}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Weekly Shipments */}
          <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl border shadow-sm relative">
            {statsLoading && (
              <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
                <div className="text-sm text-gray-400 font-medium animate-pulse">Loading Chart...</div>
              </div>
            )}
            <h3 className="font-semibold text-lg text-gray-700 mb-1">Weekly Shipments</h3>
            <p className="text-xs text-gray-400 mb-6 font-medium">Last 7 days performance</p>

            <div className="h-56">
              {weeklyShipments.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyShipments}>
                    <XAxis dataKey="day" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                    <Line type="monotone" dataKey="value" stroke="#0284c7" strokeWidth={3} dot={{ r: 4, fill: "#0284c7", strokeWidth: 2, stroke: "#fff" }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm italic">
                  No data points available
                </div>
              )}
            </div>
          </motion.div>

          {/* Docker Entry Volume */}
          <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl border shadow-sm relative">
            {statsLoading && (
              <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
                <div className="text-sm text-gray-400 font-medium animate-pulse">Loading Chart...</div>
              </div>
            )}
            <h3 className="font-semibold text-lg text-gray-700 mb-1">Docket Volume</h3>
            <p className="text-xs text-gray-400 mb-6 font-medium">Volume generated by week</p>

            <div className="h-56">
              {dockerVolume.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dockerVolume}>
                    <XAxis dataKey="week" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                    <Bar dataKey="value" fill="#7dd3fc" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm italic">
                  No data points available
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Tracking Module */}
        <motion.div variants={itemVariants} className="bg-white p-6 md:p-8 rounded-2xl border border-gray-100 shadow-sm mb-6">
          <h3 className="font-semibold text-xl mb-6 text-gray-800 flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-500" /> Track External Docket
          </h3>

          <div className="relative mb-8 max-w-3xl group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Package className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              value={searchNo}
              onChange={(e) => setSearchNo(e.target.value)}
              placeholder="Enter Docket Number (e.g. DOC-1234)"
              className="block w-full pl-12 pr-36 py-4 bg-gray-50/50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-medium text-gray-700"
            />
            <button
              onClick={handleTrack}
              disabled={loadingTracking || !searchNo}
              className="absolute right-1.5 top-1.5 bottom-1.5 px-6 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600 text-white rounded-lg font-medium text-sm transition-all shadow-sm flex items-center justify-center gap-2"
            >
              {loadingTracking ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> Tracking...</>
              ) : "Track Package"}
            </button>
          </div>

          {trackingData && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="pt-6 border-t border-gray-100">
              <div className="flex items-center justify-between mb-12">
                <h4 className="text-gray-500 font-medium tracking-wide">
                  Results for: <span className="text-gray-900 font-bold ml-1 text-lg">{trackingData.docketNumber}</span>
                </h4>
              </div>

              <div className="relative mb-10 px-4 md:px-8">
                {/* Continuous Progress Background */}
                <div className="absolute top-6 left-8 right-8 h-1.5 bg-gray-100 rounded-full z-0 overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }} 
                    animate={{ width: `${Math.max(0, (steps.findIndex(s => s === trackingData.history[trackingData.history.length - 1]?.status) / (steps.length - 1)) * 100)}%` }} 
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                    className="absolute top-0 left-0 h-full bg-blue-500" 
                  />
                </div>

                <div className="flex items-start justify-between relative z-10">
                  {steps.map((step, index) => {
                    const stepData = trackingData.history.find((h) => h.status === step);
                    const isCompleted = !!stepData;
                    const isCurrent = trackingData.history[trackingData.history.length - 1]?.status === step;
                    
                    const getIconForStep = () => {
                      if (step === "CREATED") return <Package className="w-5 h-5" />;
                      if (step === "MANIFESTED") return <ClipboardList className="w-5 h-5" />;
                      if (step === "IN_TRANSIT") return <Truck className="w-5 h-5" />;
                      if (step === "DELIVERED") return <CheckCircle2 className="w-5 h-5" />;
                      return <Package className="w-5 h-5" />;
                    };

                    return (
                      <div key={step} className="flex flex-col items-center relative group">
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: index * 0.15 }}
                          className={`w-12 h-12 flex items-center justify-center rounded-full border-[3px] shadow-sm transition-all duration-300 bg-white
                          ${isCompleted
                              ? isCurrent
                                ? "border-emerald-500 text-emerald-600 ring-4 ring-emerald-500/20 scale-110"
                                : "border-blue-500 text-blue-600"
                              : "border-gray-200 text-gray-300"
                            }`}
                        >
                          {getIconForStep()}
                        </motion.div>

                        <p className={`text-xs mt-4 font-bold tracking-wide transition-colors ${isCurrent ? 'text-gray-900' : 'text-gray-400'}`}>
                          {step.replace("_", " ")}
                        </p>

                        {stepData && (
                          <motion.div 
                            initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                            className="mt-4 bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.06)] rounded-xl px-4 py-3 text-xs w-48 text-left relative before:absolute before:-top-2 before:left-1/2 before:-translate-x-1/2 before:border-4 before:border-transparent before:border-b-white mx-auto"
                          >
                            <p className="text-gray-500 font-semibold flex items-center gap-2 border-b border-gray-50 pb-2 mb-2">
                              <span className="text-gray-400">🕒</span> {stepData.date} <span className="text-gray-300">•</span> {stepData.time ? moment(stepData.time, ["HH:mm:ss", "HH:mm"]).format("hh:mm A") : ""}
                            </p>
                            {stepData.branch && <p className="text-gray-600 truncate py-1 flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-rose-500" /> {stepData.branch}</p>}
                            {stepData.manifestId && <p className="text-gray-600 truncate py-1 flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-blue-500" /> {stepData.manifestId}</p>}
                            {stepData.tripId && <p className="text-gray-600 truncate py-1 flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-emerald-500" /> {stepData.tripId}</p>}
                          </motion.div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 🧾 Current Step Highlight Footer */}
              <div className="mt-12">
                {trackingData.history.map((h, i) => {
                  const isCurrent = i === trackingData.history.length - 1;
                  if (!isCurrent) return null;

                  const isDelivered = h.status === "DELIVERED";

                  return (
                    <motion.div 
                      key={i} 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-5 rounded-2xl border shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-6 transition-colors duration-500
                      ${isDelivered ? 'bg-emerald-50/50 border-emerald-100/50' : 'bg-blue-50/30 border-blue-100/50'}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${isDelivered ? 'bg-emerald-100 text-emerald-600' : 'bg-blue-100 text-blue-600'}`}>
                           {isDelivered ? <CheckCircle2 className="w-6 h-6" /> : <ClockIcon className="w-6 h-6" />}
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-semibold tracking-wide uppercase mb-1">Latest Update</p>
                          <p className={`font-bold text-lg ${isDelivered ? 'text-emerald-900' : 'text-blue-900'}`}>
                            {h.label || h.status?.replace("_", " ")}
                          </p>
                          <p className={`text-sm mt-0.5 font-medium ${isDelivered ? 'text-emerald-700/70' : 'text-blue-700/70'}`}>
                            Achieved on {h.date} at {h.time ? moment(h.time, ["HH:mm:ss", "HH:mm"]).format("hh:mm A") : ""}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3 flex-wrap">
                        {h.branch && (
                          <span className="bg-white/80 backdrop-blur border border-white/40 text-gray-700 px-4 py-2 rounded-lg shadow-sm text-sm font-semibold flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-rose-500" /> {h.branch}
                          </span>
                        )}
                        {h.manifestId && (
                          <span className="bg-white/80 backdrop-blur border border-white/40 text-gray-700 px-4 py-2 rounded-lg shadow-sm text-sm font-semibold flex items-center gap-2">
                            <FileText className="w-4 h-4 text-sky-500" /> {h.manifestId}
                          </span>
                        )}
                        {h.tripId && (
                          <span className="bg-white/80 backdrop-blur border border-white/40 text-gray-700 px-4 py-2 rounded-lg shadow-sm text-sm font-semibold flex items-center gap-2">
                            <Truck className="w-4 h-4 text-emerald-500" /> {h.tripId}
                          </span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Dashboard;
